import React, { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";

import ScorecardDashboard          from "./ScorecardDashboard";      // MTD and DPD
import TrendsDashboard             from "./TrendsDashboard";
import PtpUnifiedDashboard         from "./PtpUnifiedDashboard";     // PTP with Kept inside it
import PtpDashboard                from "./ptpdashboard";            // hidden; was "PTP"
import PaymentsDashboard           from "./PaymentsDashboard";       // hidden; was "Kept"
import TwentyFourHrPayDashboard    from "./TwentyFourHrPayDashboard";
import DPDDashboard                from "./Dpddashboard";
import CampaignComparisonDashboard from "./CampaignComparisonDashboard";
import CureAgingDashboard          from "./CureAgingDashboard";
import DelinquenciesDashboard      from "./DelinquenciesDashboard";
import OptOutDashboard             from "./OptoutDashboard";
import AbandonmentDashboard        from "./AbandonmentDashboard";
import ComplianceDashboard         from "./ComplianceDashboard";
import SentimentDashboard          from "./SentimentDashboard";
import LanguageDashboard           from "./Languagedashboard";

// ── THE ONLY CROSS-FOLDER IMPORT IN THIS FILE ──
// `ops` and `pbi` are siblings, so this is one level up and back down. The
// component does not move, so its own imports (./opsShared, ../../services/
// ops/opsservices) still resolve.
//
// Deliberate rather than accidental: Payment Activity is an OPS page being
// SHOWN here, not a PBI page. If a second one ever follows, the two folders
// are merging and that is a decision worth making on purpose rather than
// arriving at one import at a time.
import PaymentActivityDashboard    from "../ops/PaymentActivityDashboard";

const BLUE = "#378ADD";

/**
 * ── ONE ENTRY PER TAB, KEYED BY NAME ──
 *
 * This was three parallel structures: a SUB_TABS array of labels, a
 * HIDDEN_TABS set of indices and a SELF_CHANNEL array of indices, all
 * positional, plus a render switch on the same numbers. Its own comment warned
 * that inserting a tab anywhere silently hides the wrong one — and adding the
 * merged PTP page is exactly that insertion.
 *
 * So the three lists became fields on one object and the switch became
 * `render`. Inserting a tab is now inserting one entry, and nothing else has
 * to be renumbered.
 *
 * `key` is what the Tabs value carries, not the index, so reordering this
 * array cannot move a selection onto a different page.
 *
 *   hidden        kept in the file and routable, just not in the tab bar
 *   selfChannel   the page manages channel itself, so the header toggle is
 *                 hidden for it
 *   render        receives the header's channel; ignore it on a selfChannel
 *                 page
 */
const TABS = [
  // MTD leads the tab bar. It is the only tab that answers "compared to what" —
  // every other one measures a window against nothing but itself. It is NOT the
  // tab the page opens on; see DEFAULT_TAB below.
  //
  // MTD and DPD are selfChannel: the periods ARE the answer, and a channel
  // toggle would imply the comparison is scoped when it is not. Both carry
  // their own slicers instead.
  { key: "mtd",   label: "MTD",   selfChannel: true, render: () => <ScorecardDashboard /> },
  { key: "dpd",   label: "DPD",   selfChannel: true, render: () => <ScorecardDashboard dpd /> },

  { key: "texts", label: "Texts", render: (ch) => <TrendsDashboard channel={ch} /> },

  // ── THE MERGED PAGE ──
  // PTP with Kept drawn inside it. Replaces what were two tabs: Kept is a PART
  // of PTP, and splitting them meant the ratio between them — the number the
  // pages existed to show — could only be read by remembering the other tab.
  { key: "ptp",   label: "PTP",   render: (ch) => <PtpUnifiedDashboard channel={ch} /> },

  // ── PAYMENT ACTIVITY ──
  // Placed here because it answers "what did the agent actually collect",
  // which is the question a reader has immediately after PTP — and immediately
  // before 24-hr Pay, the manually reconciled version of the same thing.
  //
  // It reads the ledger: CF-AI3-PAY (Completed) and CF-AI4-PTP (Scheduled)
  // events in customerconvevent, the same events PTP counts, so the two tabs
  // agree and a Spanish confirmation counts like an English one. It used to
  // count confirmation MESSAGES by matching English text; that is gone.
  //
  // It is also the page the view OPENS on — see DEFAULT_TAB below.
  //
  //   selfChannel   the page carries its own slicers and has NO channel
  //                 dimension. Without this the header shows a Texts/Emails
  //                 toggle that changes nothing, and a control that does
  //                 nothing is worse than no control — the reader assumes the
  //                 numbers moved.
  //
  // Opens on 12h / Hour, the same as under Ops. Deliberately NOT overridden to
  // This Mo / Day to match the tabs beside it: a page reached from two places
  // that opens differently in each is a page two people describe differently,
  // and the money figures here are the ones most likely to be read aloud in a
  // meeting. The range pills are one click away either way.
  { key: "payments", label: "Payment Activity", selfChannel: true,
    render: () => <PaymentActivityDashboard /> },

  // ── BOTH PREDECESSORS ARE HIDDEN, NOT DELETED ──
  //
  // They stay routable so the merged page can be diffed against them while its
  // numbers are being checked — flip `hidden` off, read the same range on
  // both, flip it back.
  //
  // ⚠ PTP LEGACY IS THE ONLY HOME OF THREE TABLES: PTP by DPD bucket, by DPD
  // day, and by date. Hiding it takes them off the tab bar entirely — the
  // merged page has no equivalent. If anyone reaches for them, the fix is to
  // move them onto the merged page rather than to un-hide this.
  //
  // It also still charts `total_ptp_count`, which the merged page deliberately
  // does not — see the note at the top of PtpUnifiedDashboard. That is the one
  // figure that legitimately differs between the two, so it is the first thing
  // to check when comparing them and finding a mismatch.
  { key: "ptp_legacy", label: "PTP Legacy", hidden: true, render: (ch) => <PtpDashboard channel={ch} /> },
  { key: "kept",       label: "Kept",       hidden: true, render: (ch) => <PaymentsDashboard channel={ch} /> },

  // No channel dimension — reads payment/DPD movement derived from
  // customerdata, not from a conversation channel.
  { key: "pay24", label: "24-hr Pay", selfChannel: true, render: () => <TwentyFourHrPayDashboard /> },

  // Was "DPD (legacy)". Renamed for what a reader actually goes there for: the
  // day × hour grid. Calling it DPD put it in competition with the MTD-family
  // DPD tab, which is a different question entirely. It takes the channel prop
  // and has its own Texts/Emails toggle inside the page, so the header toggle
  // drives it.
  { key: "heatmap", label: "Heatmap", render: (ch) => <DPDDashboard channel={ch} /> },

  { key: "campaign",  label: "Campaign",           hidden: true, render: (ch) => <CampaignComparisonDashboard channel={ch} /> },
  { key: "aging",     label: "Aging",              hidden: true, selfChannel: true, render: () => <CureAgingDashboard /> },
  { key: "inflow",    label: "Inflow",             hidden: true, selfChannel: true, render: () => <DelinquenciesDashboard /> },
  { key: "optouts",   label: "Opt-outs",           hidden: true, render: (ch) => <OptOutDashboard channel={ch} /> },
  { key: "abandon",   label: "Abandonment",        hidden: true, render: (ch) => <AbandonmentDashboard channel={ch} /> },
  { key: "compliance",label: "Compliance",         hidden: true, render: (ch) => <ComplianceDashboard channel={ch} /> },
  { key: "sentiment", label: "Sentiment Analysis", hidden: true, render: (ch) => <SentimentDashboard channel={ch} /> },
  { key: "language",  label: "Language",           hidden: true, render: (ch) => <LanguageDashboard channel={ch} /> },
];

const VISIBLE = TABS.filter(t => !t.hidden);

// ── THE TAB THE VIEW OPENS ON ──
// By key, not position, so it stays on Payment Activity however the tab bar is
// reordered. The tab keeps its place in the bar; only the landing changes.
//
// If this key is ever hidden or removed, the view opens on the first visible
// tab instead of on nothing.
const DEFAULT_TAB = "payments";
const INITIAL_TAB =
  VISIBLE.some(t => t.key === DEFAULT_TAB) ? DEFAULT_TAB : VISIBLE[0].key;

export default function PowerBIManagerView() {
  const [channel, setChannel] = useState("texts");
  const [tabKey,  setTabKey]  = useState(INITIAL_TAB);

  // Falls back to the first visible tab rather than rendering nothing, so a key
  // that gets hidden later — or removed — lands somewhere real instead of
  // leaving MUI with a Tabs value matching no Tab.
  const active = TABS.find(t => t.key === tabKey && !t.hidden) || VISIBLE[0];

  return (
    <Box sx={{ width: "100%", height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>

      {/* ── Sticky header ── */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 10,
        backgroundColor: "background.paper",
        borderBottom: "0.5px solid", borderColor: "divider",
        display: "flex", alignItems: "center",
        px: 2, gap: 2,
        minHeight: 48,
      }}>
        <Tabs
          value={active.key}
          onChange={(_, v) => setTabKey(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexGrow: 1,
            minHeight: 48,
            "& .MuiTabs-indicator": {
              height: 2,
              borderRadius: "2px 2px 0 0",
              backgroundColor: BLUE,
            },
            "& .MuiTab-root": {
              minHeight: 48,
              fontSize: 13,
              fontWeight: 400,
              textTransform: "none",
              color: "text.secondary",
              px: 1.5,
              "&.Mui-selected": {
                color: BLUE,
                fontWeight: 500,
              },
            },
          }}
        >
          {VISIBLE.map(t => (
            <Tab key={t.key} label={t.label} value={t.key} disableRipple />
          ))}
        </Tabs>

        {/* Global channel toggle — hidden for pages that manage channel themselves */}
        {!active.selfChannel && (
          <Box sx={{
            display: "flex",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: "20px",
            overflow: "hidden",
            flexShrink: 0,
          }}>
            {["texts", "emails"].map(ch => (
              <Box
                key={ch}
                onClick={() => setChannel(ch)}
                sx={{
                  px: "16px", py: "5px",
                  fontSize: 13,
                  fontWeight: channel === ch ? 500 : 400,
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all .15s",
                  background:  channel === ch ? BLUE : "transparent",
                  color:       channel === ch ? "#fff" : "text.secondary",
                  "&:hover": {
                    background: channel === ch ? BLUE : "action.hover",
                  },
                }}
              >
                {ch.charAt(0).toUpperCase() + ch.slice(1)}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {active.render(channel)}
      </Box>
    </Box>
  );
}